import { assign, concat, differenceBy, filter, find, isNil, map, some, unionBy } from 'lodash';
import { PropsWithChildren, startTransition, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Button, ButtonGroup, Form, FormGroup, Input, Label, Navbar } from 'reactstrap';
import { MapContext, MapType } from '../context';
import styled from '../themes/themed-styled-components';
import { RiskAssessment, Terminal, TerminalArea } from '../types/graph';
import WorkerApi from '../worker';
import Loading from './Loading';
import { TerminalMapViewInput } from '../worker/worker-api-type';
import { useCurrentUser } from '../hooks';
import { DeepPartial } from 'tsdef';
import { GridLayout } from './ui';
import { RoleName } from '../types';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

const googleMapsUrl = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyBLDm3MJ-vOxZGSNMyo8yGTXI05A6sw7uc&callback=initGoogleMaps';

const loadGoogleMaps = new Promise<void>((resolve, reject) => {
    if (!document.querySelectorAll(`[src="${googleMapsUrl}"]`).length) {
        (window as any)['initGoogleMaps'] = resolve;
        document.body.appendChild(Object.assign(
            document.createElement('script'), {
            type: 'text/javascript',
            src: googleMapsUrl,
            onError: reject
        }));
        return;
    } 
    resolve();
});

export const useMap = () => useContext(MapContext);

const maps: google.maps.Map[] = [];

const MapContainer = styled.div`
    &, & > div {
        height: 100%;
        width: 100%
    }
`;



type GoogleMapProps = PropsWithChildren<{
    zoom: number,
    center: { lat: number, lng: number },
    onClick?: ((args: { latLng: google.maps.LatLng }) => void),
    onViewChange?: ((args: { center: google.maps.LatLng, zoom: number, bounds: google.maps.LatLngBounds }) => void),
}>;

const Map = ({ zoom, center, onClick, onViewChange, children }: GoogleMapProps) => {
    const [mapsReady, setMapsReady] = useState<boolean>(false);
    const [mapVisible, setMapVisible] = useState<boolean>(false);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [mapCanvas, setMapCanvas] = useState<HTMLDivElement | null>(null);



    const mapContainerRef = useCallback<(node: HTMLDivElement) => void>(node => {
        if (node === null) {
            return;
        }

        let map = maps.pop() ?? null;

        let canvas: HTMLDivElement = map !== null
            ? map.getDiv() as HTMLDivElement
            : document.createElement('div');

        setMapCanvas(canvas);
        node.appendChild(canvas);

    }, []);

    useEffect(() => {
        let load = true;

        loadGoogleMaps.then(() => {
            if (load === true) {
                setMapsReady(true);
            };
        });

        return () => {
            load = false;
        };
    }, []);

    useEffect(() => {
        if (mapsReady === true && mapCanvas !== null) {
            const gmapsCenter = new google.maps.LatLng(center.lat, center.lng);

            if (map === null) {
                setMap(new google.maps.Map(mapCanvas as HTMLDivElement, {
                    zoom: zoom,
                    center: gmapsCenter,
                    mapTypeId: google.maps.MapTypeId.SATELLITE,
                    streetViewControl: false,
                    fullscreenControl: false,
                    clickableIcons: false
                }));

                //let tilesLoadedListener = google.maps.event.addListenerOnce(m, 'tilesloaded', (e) => { setMap(m) });

                //return (() => {
                //    google.maps.event.removeListener(tilesLoadedListener);
                //});
            } else if (!mapVisible) {
                map.setZoom(zoom);
                map.setCenter(gmapsCenter);
            }
        }
    }, [mapsReady, mapCanvas, map, center, zoom, mapVisible]);

    useEffect(() => {
        if (mapsReady === true && map !== null) {
            if (map.getBounds() !== undefined) {
                setMapVisible(true);
            } else {
                let mapBoundsChangedListener = google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                    setMapVisible(true);
                });

                return () => {
                    google.maps.event.removeListener(mapBoundsChangedListener);
                };
            }
        }
    }, [mapsReady, map]);

    useEffect(() => {
        if (mapsReady === true && map !== null && onchange !== undefined) {
            let mapBoundsChangedListener = google.maps.event.addListener(map, 'bounds_changed', () => {
                if (map !== null) {
                    const center = map.getCenter();
                    const zoom = map.getZoom();
                    const bounds = map.getBounds();

                    if (center !== undefined && zoom !== undefined && bounds !== undefined) {
                        onViewChange?.({ center, zoom, bounds });
                    }
                }
            });

            return () => {
                google.maps.event.removeListener(mapBoundsChangedListener);
            };
        }
    }, [mapsReady, map, onViewChange]);

    useEffect(() => {
        if (mapsReady === true && map !== null) {
            let mapOnClickListener = google.maps.event.addListener(map, 'click', ({ latLng }: google.maps.MapMouseEvent) => {
                if (latLng === null) {
                    return;
                }
                onClick?.({ latLng });
            });

            return () => {
                google.maps.event.removeListener(mapOnClickListener);
            };
        }
    }, [mapsReady, map, onClick]);

    //useEffect(() => {
    //    if (mapCanvas !== null) {
    //        return () => {
    //            canvasContainer.appendChild(mapCanvas);
    //        };
    //    }
    //}, [mapCanvas]);

    useEffect(() => {
        if (map !== null) {
            return () => {
                maps.push(map);
            }
        }
    }, [map]);

    if (!mapsReady) {
        return <Loading />;
    }

    return <MapContext.Provider value={{ map, type: MapType.GOOGLE_MAPS, ready: mapsReady && mapVisible }}>
        <MapContainer ref={mapContainerRef}>{children}</MapContainer>
    </MapContext.Provider>
}

type AreaCircleClickEvent = { mapEvent: google.maps.MapMouseEvent, circle: google.maps.Circle, infoElement: HTMLElement, area: DeepPartial<TerminalArea> }

const RED = 'rgb(255,0,0)';
const ORANGE = 'rgb(255,127,0)';
const YELLOW = 'rgb(255,255,0)';
const GREEN = 'rgb(0,255,0)';

type TerminalMapAreaProps = {
    area: DeepPartial<TerminalArea>,
    terminal: DeepPartial<Terminal>,
    riskAssessment: DeepPartial<RiskAssessment> | null,
    active?: boolean,
    onMarkerClick?: () => void,
    onCircleClick?: (ev: AreaCircleClickEvent) => void,
    onMarkerPositionChange?: (args: { latLng: google.maps.LatLng }) => void,
    editMode?: boolean,
    selectable?: boolean
};

const TerminalMapArea = ({ terminal, area, riskAssessment, active = false, editMode = false, selectable = false, onMarkerClick, onCircleClick, onMarkerPositionChange }: TerminalMapAreaProps) => {
    const { map, ready } = useMap();
    const [circle, setCircle] = useState<google.maps.Circle | null>(null);
    const [marker, setMarker] = useState<google.maps.Marker | null>(null);
    const [infoContainer, setInfoContainer] = useState(document.createElement('div'));

    const areaName = area.name as string;
    const areaDescription = area.description as string | null;
    const areaCenterLatitude = area.centerLatitude as number;
    const areaCenterLongitude = area.centerLongitude as number;
    //const radius = riskAssessment?.radius ?? 0;

    const circleColor = useMemo(() => {
        if (riskAssessment === null) {
            return null;
        }

        const { radius, highRadius } = riskAssessment;
        const { alarmThreshold, alertThreshold, warningThreshold } = area;

        if (isNil(radius)) {
            return null;
        }

        if (!isNil(alarmThreshold) && radius > alarmThreshold) {
            return RED;
        }

        if (!isNil(alertThreshold) && radius > alertThreshold) {
            return ORANGE;
        }

        if (!isNil(warningThreshold) && radius > warningThreshold) {
            return YELLOW;
        }


        return highRadius === true ? RED : GREEN
    }, [riskAssessment, area]);

    useEffect(() => {
        if (map !== null && ready === true) {

            let m = new google.maps.Marker({
                visible: false,
                map
            });

            let c = new google.maps.Circle({
                visible: false,
                fillOpacity: 0,
                map
            });

            setMarker(m);
            setCircle(c);

            google.maps.event.addListener(c, 'click', function (this: google.maps.Circle, ev: google.maps.MapMouseEvent) {
                map?.fitBounds(c?.getBounds() as google.maps.LatLngBounds);
                onCircleClick?.({ mapEvent: ev, circle: this, area, infoElement: infoContainer });
            });

            return () => {
                m.setMap(null);
                c.setMap(null);
            }
        }
    }, [map, ready, onCircleClick, area, infoContainer]);

    useEffect(() => {
        if (circle === null || riskAssessment === null) {
            return;
        }

        circle.setRadius(riskAssessment.radius??0)

    }, [circle, terminal, riskAssessment])

    useEffect(() => {
        if (circle === null) {
            return;
        }

        circle.setOptions({ strokeColor: circleColor})

    }, [circleColor, circle])

    useEffect(() => {
        if (marker !== null && onMarkerClick !== undefined) {

            const listener = google.maps.event.addListener(marker, 'click', () => {
                onMarkerClick();
            });

            return () => google.maps.event.removeListener(listener);
        }
    }, [marker, onMarkerClick]);

    useEffect(() => {
        if (marker !== null && onMarkerPositionChange !== undefined) {

            const listener = google.maps.event.addListener(marker, 'dragend', ({ latLng }: google.maps.MapMouseEvent) => {
                if (latLng === null) {
                    return;
                }
                onMarkerPositionChange({ latLng });
            });

            return () => google.maps.event.removeListener(listener);
        }
    }, [marker, onMarkerPositionChange]);

    useEffect(() => {

        if (ready === true && map !== null) {

            let latLng = new google.maps.LatLng(area.centerLatitude as number, area.centerLongitude as number);
            let showCircle = (editMode === false && ((riskAssessment?.radius ?? null) !== null));
            let showMarker = editMode;

            if (circle !== null && circle.getVisible() !== showCircle) {
                const radius = riskAssessment?.radius ?? 0;

                circle.setOptions({
                    visible: showCircle,
                    center: latLng,
                    radius,
                    zIndex: 0 - radius
                });

                if (showCircle) {
                    let mapBounds = map.getBounds();
                    let circleBounds = circle?.getBounds();

                    if (circleBounds !== null && mapBounds !== undefined) {
                        if (!mapBounds.contains(circleBounds.getNorthEast()) || !mapBounds.contains(circleBounds.getSouthWest())) {
                            map.fitBounds(mapBounds.union(circleBounds));
                        }
                    }
                }
            }

            if (marker !== null && marker.getVisible() !== showMarker) {
                marker.setOptions({
                    visible: showMarker,
                    position: latLng,
                    label: areaName,
                    title: areaDescription
                });
            }
        }
    }, [area, riskAssessment, map, ready, editMode, marker, circle, areaName, areaDescription])

    useEffect(() => {
        if (marker !== null) {
            marker.setDraggable(active);
            marker.setClickable(selectable);
            marker.setOpacity(selectable ? 1 : 0.5);
        }
    }, [marker, active, selectable]);

    useEffect(() => {
        if (marker !== null) {

            const markerPosition = marker.getPosition();

            if (markerPosition?.lat() !== areaCenterLatitude || markerPosition?.lng() !== areaCenterLongitude) {

                marker.setPosition({ lat: areaCenterLatitude as number, lng: areaCenterLongitude as number });
            }
        }
    }, [marker, areaCenterLatitude, areaCenterLongitude]);

    useEffect(() => {
        if (marker !== null) {
            marker.setLabel(areaName);
            marker.setTitle(areaDescription);
        }
    }, [marker, areaName, areaDescription]);

    if (isNil(riskAssessment)) {
        return null;
    }

    return createPortal(<div style={{ marginTop: '1em' }}>
        <h5><Link to={`/${terminal.slug}/rischio/${area.name}`}>Area {area.name}</Link></h5>
        {area.description && <h6>Area {area.description}</h6>}
        <dl>
            <dt>Raggio</dt>
            <dd>{ riskAssessment.radius } m</dd>
            <dt>Posizione</dt>
            <dd><Link to={`/${terminal.slug}/rischio/${riskAssessment.area}/${riskAssessment.radiusBay}/${riskAssessment.radiusRow}`}>{riskAssessment.radiusBay}/{riskAssessment.radiusRow}</Link></dd>
        </dl>
    </div>, infoContainer);
};

type TerminalViewEditProps = {
    terminal: DeepPartial<Terminal>,
    onCenterChange?: (args: { latLng: google.maps.LatLng }) => void,
};

const TerminalViewEdit = ({ terminal: { centerLatitude = null, centerLongitude = null, northLatitude = null, southLatitude = null, westLongitude = null, eastLongitude = null }, onCenterChange }: TerminalViewEditProps) => {
    const { map, ready } = useMap();
    const [marker, setMarker] = useState<google.maps.Marker | null>(null);
    //const [rectangle, setRectangle] = useState<google.maps.Rectangle | null>(null);

    useEffect(() => {
        if (map !== null && ready === true) {
            let m = new google.maps.Marker({
                draggable: true,
                map
            });

            let r = new google.maps.Rectangle({
                visible: false,
                strokeColor: 'blue',
                fillOpacity: 0,
                map
            });

            setMarker(m);
            //setRectangle(r);

            return () => {
                m.setMap(null);
                r.setMap(null);
            }
        }
    }, [map, ready]);

    useEffect(() => {
        if (marker !== null && onclick !== undefined) {
            const listener = google.maps.event.addListener(marker, 'dragend', ({ latLng }: google.maps.MapMouseEvent) => {
                if (latLng === null) {
                    return;
                }
                onCenterChange?.({ latLng });
            });

            return () => google.maps.event.removeListener(listener);
        }
    }, [marker, onCenterChange]);

    const center = useMemo(() => {
        console.log({ centerLatitude, centerLongitude });
        if (centerLatitude === null || centerLongitude === null) {
            return null;
        }

        return new google.maps.LatLng(centerLatitude, centerLongitude);
    }, [centerLatitude, centerLongitude]);

    useEffect(() => {
        const visible = center !== null;
        if (visible) {
            marker?.setPosition(center);
        }

        marker?.setVisible(visible);

    }, [marker, center]);

    return null;
};

type TerminalMapProps = {
    terminal: DeepPartial<Terminal>
};

enum TerminalMapMode {
    VIEW,
    EDIT_TERMINAL_VIEW,
    EDIT_AREAS
}

type TerminalAreasReducerState = {
    added: DeepPartial<TerminalArea>[],
    updated: DeepPartial<TerminalArea>[],
    deleted: DeepPartial<TerminalArea>[]
}

enum TerminalAreasReducerActionType {
    ADD,
    UPDATE,
    DELETE,
    CLEAR
}

type TerminalAreasReducerAction = {
    type: TerminalAreasReducerActionType
    area: DeepPartial<TerminalArea>;
}

const terminalAreasReducer = ({ added, updated, deleted }: TerminalAreasReducerState, { type, area }: TerminalAreasReducerAction) => {
    switch (type) {
        case TerminalAreasReducerActionType.ADD:
            return { added: concat(added, [area]), updated, deleted };
        case TerminalAreasReducerActionType.UPDATE:
            return { added: differenceBy(added, [area], 'id'), updated: unionBy([area], updated, 'id'), deleted };
        case TerminalAreasReducerActionType.DELETE:
            return { added: differenceBy(added, [area], 'id'), updated: differenceBy(updated, [area], 'id'), deleted: unionBy([area], deleted, 'id') };
        case TerminalAreasReducerActionType.CLEAR:
            return { added: [], updated: [], deleted: [] };
        default:
            throw new Error();
    }
}

const TerminalMap = ({ terminal }: TerminalMapProps) => {
    //const { user } = useContext(UserContext);
    const [mode, setMode] = useState<TerminalMapMode>(TerminalMapMode.VIEW);
    const [dirtyTerminalView, setDirtyTerminalView] = useState<DeepPartial<Terminal> | null>(null);
    const [updatedTerminalView, setUpdatedTerminalView] = useState<DeepPartial<Terminal> | null>(null);
    const [activeArea, setActiveArea] = useState<DeepPartial<TerminalArea> | null>(null);
    const [dirtyArea, setDirtyArea] = useState<DeepPartial<TerminalArea> | null>(null);
    const [{ added: addedAreas, updated: updatedAreas, deleted: deletedAreas }, terminalAreasDispatch] = useReducer(terminalAreasReducer, { added: [], updated: [], deleted: [] });
    const [actualZoom, setActualZoom] = useState<number | null>(null);
    const [actualCenter, setActualCenter] = useState<google.maps.LatLng | null>(null);

    const infoWindowRef = useRef(new google.maps.InfoWindow());

    const { isSuperuser, getRolesForTerminal } = useCurrentUser();

    const canEditAreas = isSuperuser === true || some(getRolesForTerminal(terminal?.id ?? 0), (r) => r.name === RoleName.ADMIN);

    //const [actualBounds, setActualBounds] = useState<google.maps.LatLngBounds | null>(null);

    const { id: terminalId, areas, riskAssessments, centerLatitude = null, centerLongitude = null, mapZoom = null, northLatitude = null, southLatitude = null, westLongitude = null, eastLongitude = null } = useMemo(() => assign({}, terminal, updatedTerminalView, dirtyTerminalView), [terminal, updatedTerminalView, dirtyTerminalView]);

    //console.log({ centerLatitude, centerLongitude, mapZoom });

    const areasRiskAssessments = useMemo(() => filter(riskAssessments, (r) => r?.area !== "TERMINAL"), [riskAssessments]);

    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => () => terminalAreasDispatch({ type: TerminalAreasReducerActionType.CLEAR, area: {} as TerminalArea }), [areas]);

    const visibleAreas = useMemo(() => differenceBy(unionBy(dirtyArea === null ? [] : [dirtyArea], updatedAreas, addedAreas, areas, 'id'), deletedAreas, 'id'), [areas, dirtyArea, addedAreas, updatedAreas, deletedAreas])

    const terminalHasMapViewNavigation = useMemo(() => ((centerLatitude === null || centerLongitude === null || mapZoom === null) !== true), [centerLatitude, centerLongitude, mapZoom]);
    //const terminalHasMapViewBounds = useMemo(() => ((northLatitude === null || southLatitude === null || westLongitude === null || eastLongitude === null) !== true), [northLatitude, southLatitude, westLongitude, eastLongitude]);

    const dirtyAreaIsValid = useMemo(() => {
        if (dirtyArea === null) return false;

        if ((dirtyArea.name ?? '').length === 0) return false;

        if (find(differenceBy(visibleAreas, [dirtyArea], 'id'), ['name', dirtyArea.name as string])) return false;

        return true;
    }, [dirtyArea, visibleAreas]);


    const initDirtyTerminalView = useCallback(() => {
        setDirtyTerminalView(assign({}, dirtyTerminalView, { id: terminalId, centerLatitude, centerLongitude, mapZoom, northLatitude, southLatitude, westLongitude, eastLongitude }));
    }, [dirtyTerminalView, terminalId, centerLatitude, centerLongitude, mapZoom, northLatitude, southLatitude, westLongitude, eastLongitude]);

    const setDirtyTerminalCenter = useMemo(() => ({ latLng }: { latLng: google.maps.LatLng }) => {
        setDirtyTerminalView(assign({}, dirtyTerminalView, { centerLatitude: latLng.lat(), centerLongitude: latLng.lng() }));
    }, [dirtyTerminalView]);

    const removeDirtyTerminalCenter = useCallback(() => {
        setDirtyTerminalView(assign({}, dirtyTerminalView, { centerLatitude: null, centerLongitude: null }));
    }, [dirtyTerminalView]);

    const setDirtyTerminalCenterToMapCenter = useCallback(() => {
        if (actualCenter === null) return;

        setDirtyTerminalView(assign({}, dirtyTerminalView, { centerLatitude: actualCenter.lat(), centerLongitude: actualCenter.lng() }));
    }, [dirtyTerminalView, actualCenter]);

    const setDirtyTerminalCenterToMapZoom = useCallback(() => {
        if (actualZoom === null) return;

        setDirtyTerminalView(assign({}, dirtyTerminalView, { mapZoom: actualZoom }));
    }, [dirtyTerminalView, actualZoom]);

    const toolbar = useMemo(() => {
        if (canEditAreas === false) return null;

        if (mode === TerminalMapMode.VIEW) {
            return <Navbar className="navbar ng-white border-bottom box-shadow" light>
                {/*<NavbarBrand tag={Link} to="/">{t('app.title')}</NavbarBrand>*/}
                <div className="d-sm-inline-flex flex-sm-row gap-2">
                    <Button className="mr-sm-2 form-control text-nowrap" onClick={() => startTransition(() => {
                        setMode(TerminalMapMode.EDIT_TERMINAL_VIEW);
                        initDirtyTerminalView();
                    })}>Modifica vista terminal</Button>
                    <Button disabled={!terminalHasMapViewNavigation} className="mr-sm-2 form-control" onClick={() => setMode(TerminalMapMode.EDIT_AREAS)}>Modifica aree</Button>
                </div>
                <div className="navbar-right"></div>
            </Navbar>;
        }

        if (mode === TerminalMapMode.EDIT_TERMINAL_VIEW && dirtyTerminalView !== null) {
            return <Navbar className="navbar ng-white border-bottom box-shadow" light>
                {/*<NavbarBrand tag={Link} to="/">{t('app.title')}</NavbarBrand>*/}
                <div className="d-sm-inline-flex flex-sm-row gap-2 align-items-center">
                    <span className="text-nowrap mr-sm-2"><strong>Modifica vista terminal</strong></span>

                    <Form tag="div" className="d-sm-inline-flex flex-sm-row gap-3">
                        <FormGroup noMargin>
                            {centerLatitude !== null && centerLongitude !== null
                                ? <Button onClick={() => removeDirtyTerminalCenter()}>Rimuovi centro</Button>
                                : <Button onClick={() => setDirtyTerminalCenterToMapCenter()}>Imposta centro</Button>
                            }
                        </FormGroup>
                        <FormGroup noMargin className="d-sm-inline-flex flex-sm-row gap-1 align-items-center">
                            <Label for="terminalViewZoom" className="mb-0">Zoom</Label>
                            <ButtonGroup className="d-sm-inline-flex flex-sm-row">
                                <Input disabled={saving} type="number" min="0" name="zoom" id="terminalViewZoom" autoComplete={'off'} value={dirtyTerminalView.mapZoom ?? ""} onChange={(ev) => setDirtyTerminalView(assign({}, dirtyTerminalView, { mapZoom: ev.target.value === "" ? null : Number(ev.target.value) } as DeepPartial<TerminalArea>))} />
                                <Button disabled={saving} onClick={() => setDirtyTerminalCenterToMapZoom()}>Attuale</Button>
                            </ButtonGroup>
                        </FormGroup>
                    </Form>
                </div>
                <div className="navbar-right d-sm-inline-flex flex-sm-row align-items-center gap-1">
                    <Button className="ml-sm-2 form-control" color="primary" disabled={saving} onClick={() => startTransition(() => {
                        WorkerApi.updateTerminalMapView({ terminalMapView: dirtyTerminalView as TerminalMapViewInput }).then((terminal) => {
                            setDirtyTerminalView(null);
                            setSaving(false);
                            if (terminal !== null) {
                                setUpdatedTerminalView(terminal as DeepPartial<Terminal>);
                            }
                            setMode(TerminalMapMode.VIEW);
                        });
                    })}>Salva</Button>
                    <Button className="ml-sm-2 form-control" disabled={saving} onClick={() => startTransition(() => {
                        setDirtyTerminalView(null);
                        setMode(TerminalMapMode.VIEW);
                    })}>Annulla</Button>
                </div>
            </Navbar>;
        }

        if (dirtyArea !== null) {
            return <Navbar className="navbar ng-white border-bottom box-shadow" light>
                {/*<NavbarBrand tag={Link} to="/">{t('app.title')}</NavbarBrand>*/}
                <div className="d-sm-inline-flex flex-sm-row align-items-center gap-3">
                    <span className="text-nowrap mr-sm-2"><strong>{dirtyArea.id === null ? "Aggiungi area" : "Modifica area"}</strong></span>

                    <Form tag="div" className="d-sm-inline-flex flex-sm-row align-items-center" inline>
                        <FormGroup noMargin className="d-sm-inline-flex flex-sm-row align-items-center gap-2">
                            <Label for="areaName" className="mr-sm-2 mb-0">Nome</Label>
                            <Input
                                type="text"
                                name="name"
                                id="areaName"
                                autoComplete={'off'}
                                value={dirtyArea.name}
                                disabled={saving}
                                minLength={1}
                                maxLength={8}
                                onChange={(ev) => setDirtyArea({ ...dirtyArea, name: ev.target.value })} />
                        </FormGroup>

                        <Form tag="div" className="d-sm-inline-flex flex-sm-row align-items-center" inline>
                            <FormGroup noMargin className="d-sm-inline-flex flex-sm-row align-items-center gap-2">
                                <Label for="areaDescription" className="mr-sm-2 mb-0">Descrizione</Label>
                                <Input
                                    type="text"
                                    name="description"
                                    id="areaDescription"
                                    autoComplete={'off'}
                                    value={dirtyArea.description ?? ''}
                                    disabled={saving}
                                    maxLength={50}
                                    onChange={(ev) => {
                                        const value = ev.target.value;
                                        const description = value.length > 0 ? value : null;
                                        setDirtyArea({ ...dirtyArea, description })
                                    }} />
                            </FormGroup>
                        </Form>
                    </Form>
                </div>
                <div className="navbar-right d-sm-inline-flex flex-sm-row align-items-center  gap-1">
                    <Button className="ml-sm-2 form-control" color="primary" disabled={!dirtyAreaIsValid || saving} onClick={() => {
                        setSaving(true);
                        const { id, name, description, centerLatitude, centerLongitude } = dirtyArea;
                        if (dirtyArea.id === null) {
                            WorkerApi.addTerminalArea({ terminalArea: { id, terminalId, name, description, centerLatitude, centerLongitude } }).then((terminalArea) => startTransition(() => {
                                setActiveArea(null);
                                setDirtyArea(null);
                                setSaving(false);
                                if (terminalArea !== null) {
                                    terminalAreasDispatch({ type: TerminalAreasReducerActionType.ADD, area: terminalArea });
                                }
                            }));
                        } else {
                            WorkerApi.updateTerminalArea({ terminalArea: { id, terminalId, name, description, centerLatitude, centerLongitude } }).then((terminalArea) => startTransition(() => {
                                setActiveArea(null);
                                setDirtyArea(null);
                                setSaving(false);
                                if (terminalArea !== null) {
                                    terminalAreasDispatch({ type: TerminalAreasReducerActionType.UPDATE, area: terminalArea });
                                }
                            }));
                        }
                    }}>Salva</Button>
                    <Button className="ml-sm-2 form-control" disabled={saving} onClick={() => startTransition(() => {
                        setActiveArea(null);
                        setDirtyArea(null);
                    })}>Annulla</Button>
                    {isNil(dirtyArea.id) ||
                        <Button className="ml-sm-2 form-control" color="danger" disabled={!dirtyAreaIsValid || saving} onClick={() => {
                            const terminalAreaId = dirtyArea.id!;

                            setSaving(true);

                            WorkerApi.deleteTerminalArea({ terminalAreaId }).then((result) => startTransition(() => {
                                startTransition(() => {
                                    setActiveArea(null);
                                    setDirtyArea(null);
                                    setSaving(false);
                                    if (result.isOk === true) terminalAreasDispatch({ type: TerminalAreasReducerActionType.DELETE, area: dirtyArea });
                                })
                            }));

                        }}>Elimina</Button> }
                </div>
            </Navbar>;
        }

        return <Navbar className="navbar ng-white border-bottom box-shadow" light>
            {/*<NavbarBrand tag={Link} to="/">{t('app.title')}</NavbarBrand>*/}
            <div className="d-sm-inline-flex flex-sm-row">
                <span className="text-nowrap"><strong>Modifica aree:</strong> fai clic su un segnaposto per <i>modificare</i> o su un punto della mappa per <i>aggiungere</i></span>
            </div>
            <div className="navbar-right d-sm-inline-flex flex-sm-row">
                <Button className="ml-sm-2 form-control" color="primary" onClick={() => {
                    setMode(TerminalMapMode.VIEW);
                }}>Fatto</Button>
            </div>
        </Navbar>;
    }, [terminalId, canEditAreas, mode, dirtyArea, dirtyTerminalView, saving, setDirtyTerminalCenterToMapZoom, setDirtyTerminalCenterToMapCenter, removeDirtyTerminalCenter, centerLongitude, centerLatitude, dirtyAreaIsValid, initDirtyTerminalView, terminalHasMapViewNavigation]);

    const editAreasMode = useMemo(() => mode === TerminalMapMode.EDIT_AREAS, [mode])

    const onCircleClick = useCallback((ev: AreaCircleClickEvent) => {
        const { circle, infoElement } = ev;
        const infoWindow = infoWindowRef.current;

        infoWindow.setPosition(circle.getCenter());
        infoWindow.setContent(infoElement);

        infoWindow.open({
            anchor: ev.circle
        })
    }, [])

    const areasOnMap = useMemo(() => map(visibleAreas, (area) => {
        if (isNil(area)) {
            return null;
        }

        const active = area === activeArea || area.id === activeArea?.id;

        return <TerminalMapArea
            key={area.id ?? 0}
            terminal={terminal}
            area={area}
            riskAssessment={find(areasRiskAssessments, r => r?.area === area.name) ?? null}
            editMode={editAreasMode}
            selectable={active || activeArea === null}
            active={active}
            onMarkerClick={editAreasMode ? () => startTransition(() => {
                setActiveArea(area);
                setDirtyArea(area);
            }) : undefined}
            onCircleClick={onCircleClick}
            onMarkerPositionChange={active ? ({ latLng }) => setDirtyArea(assign({}, area, { centerLatitude: latLng.lat(), centerLongitude: latLng.lng() } as TerminalArea)) : undefined}
        />
    }), [visibleAreas, activeArea, terminal, areasRiskAssessments, editAreasMode, onCircleClick]);

    if ((canEditAreas || terminalHasMapViewNavigation) !== true) {
        return null;
    }

    return <GridLayout header={toolbar}>
        <Map zoom={mapZoom ?? 4} center={{ lat: centerLatitude ?? 0, lng: centerLongitude ?? 0 }} onClick={({ latLng }) => {
            if (mode === TerminalMapMode.EDIT_AREAS && activeArea === null) {
                const newArea: Partial<TerminalArea> = {
                    id: null,
                    terminalId: terminalId as number,
                    name: "",
                    centerLatitude: latLng.lat(),
                    centerLongitude: latLng.lng()
                };

                setDirtyArea(newArea);
                setActiveArea(newArea);
            }
        }} onViewChange={({ center, zoom, bounds }) => {
            setActualCenter(center);
            setActualZoom(zoom);
        }}>
            {mode === TerminalMapMode.EDIT_TERMINAL_VIEW ? <TerminalViewEdit terminal={{ centerLatitude, centerLongitude, mapZoom, northLatitude, southLatitude, westLongitude, eastLongitude }} onCenterChange={setDirtyTerminalCenter} /> : null}
            {areasOnMap}
        </Map>
    </GridLayout>;
};

export default TerminalMap;